import { forwardRef, type ElementType, type HTMLAttributes, type AnchorHTMLAttributes, type ReactNode } from 'react';
import classnames from 'src/util/classnames';

interface NavProps extends HTMLAttributes<HTMLUListElement> {
  tabs?: boolean;
  pills?: boolean;
  fill?: boolean;
  justified?: boolean;
  vertical?: boolean | string;
  horizontal?: string;
  navbar?: boolean;
  card?: boolean;
  tag?: ElementType;
  children?: ReactNode;
}

export function Nav({ tabs, pills, fill, justified, vertical, horizontal, navbar, card, tag: Tag = 'ul', className, children, ...props }: NavProps) {
  return (
    <Tag
      className={classnames(
        navbar ? 'navbar-nav' : 'nav',
        tabs && 'nav-tabs',
        pills && 'nav-pills',
        fill && 'nav-fill',
        justified && 'nav-justified',
        card && 'card-header-tabs',
        vertical === true ? 'flex-column'
          : typeof vertical === 'string' ? `flex-${vertical}-column`
          : undefined,
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}

interface NavItemProps extends HTMLAttributes<HTMLLIElement> {
  active?: boolean;
  children?: ReactNode;
}

export function NavItem({ active, className, children, ...props }: NavItemProps) {
  return (
    <li className={classnames('nav-item', active && 'active', className)} {...props}>
      {children}
    </li>
  );
}

interface NavLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  disabled?: boolean;
  tag?: ElementType;
  children?: ReactNode;
}

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { active, disabled, tag, className, children, ...props },
  ref,
) {
  const Tag = (tag ?? (props.href ? 'a' : 'button')) as ElementType;
  return (
    <Tag
      ref={ref}
      type={Tag === 'button' ? 'button' : undefined}
      className={classnames('nav-link', active && 'active', disabled && 'disabled', className)}
      aria-disabled={disabled}
      {...props}
    >
      {children}
    </Tag>
  );
});

interface NavbarProps extends HTMLAttributes<HTMLElement> {
  light?: boolean;
  dark?: boolean;
  full?: boolean;
  fixed?: string;
  sticky?: string;
  color?: string;
  expand?: boolean | string;
  container?: boolean | string;
  tag?: ElementType;
  children?: ReactNode;
}

export function Navbar({ light, dark, full, fixed, sticky, color, expand, container: _container, tag: Tag = 'nav', className, children, ...props }: NavbarProps) {
  return (
    <Tag
      className={classnames(
        'navbar',
        light && 'navbar-light',
        dark && 'navbar-dark',
        color && `bg-${color}`,
        expand === true ? 'navbar-expand'
          : typeof expand === 'string' ? `navbar-expand-${expand}`
          : undefined,
        fixed && `fixed-${fixed}`,
        sticky && `sticky-${sticky}`,
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function NavbarBrand({ tag: Tag = 'a', className, children, ...props }: HTMLAttributes<HTMLAnchorElement> & { tag?: ElementType }) {
  return <Tag className={classnames('navbar-brand', className)} {...props}>{children}</Tag>;
}

export function NavbarText({ className, children, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={classnames('navbar-text', className)} {...props}>{children}</span>;
}

export function NavbarToggler({ className, children, ...props }: HTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={classnames('navbar-toggler', className)} {...props}>
      {children ?? <span className="navbar-toggler-icon" />}
    </button>
  );
}
